"use client";
import { useState, useEffect } from "react";
import {
  FiSearch,
  FiCalendar,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiBookmark,
  FiShare2,
  FiHeart,
} from "react-icons/fi";

import { BlogPost } from "../types/blogTyps/BlogPostDetail";
import { categories } from "../data/blogData/BlogCategoriData";
import { posts } from "../data/blogData/BlogArticalsData";
import { encryptId } from "../components/encryption/encryption";
import BreadcrumbsComponent from "../components/common/BreadcrumbsComponent";
import { Link } from "react-router-dom";

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(posts);
  const [isClient, setIsClient] = useState(false);
  const postsPerPage = 6;

  // Calculate real category counts
  const [categoryCounts, setCategoryCounts] = useState<{ name: string, count: number }[]>([]);

  useEffect(() => {
    setIsClient(true);

    // Calculate initial category counts
    const counts = categories.map(category => {
      if (category.name === "All") {
        return { name: category.name, count: posts.length };
      }
      const count = posts.filter(post => post.category === category.name).length;
      return { name: category.name, count };
    });

    setCategoryCounts(counts);
  }, []);

  useEffect(() => {
    let results = [...posts];

    // Filter by search query
    if (searchQuery) {
      results = results.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.tags.some((tag: string) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
    }

    // Filter by category
    if (activeCategory && activeCategory !== "All") {
      results = results.filter((post) => post.category === activeCategory);
    }

    setFilteredPosts(results);
    setCurrentPage(1);

    // Update category counts whenever posts or filters change
    const updatedCounts = categories.map(category => {
      if (category.name === "All") {
        return { name: category.name, count: posts.length };
      }

      // For filtered counts
      const count = results.filter(post => post.category === category.name).length;
      return { name: category.name, count };
    });

    setCategoryCounts(updatedCounts);
  }, [searchQuery, activeCategory]);

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const handleLike = (postId: number) => {
    const updatedPosts = filteredPosts.map((post) =>
      post.id === postId
        ? {
          ...post,
          isLiked: !post.isLiked,
          likes: post.isLiked ? post.likes - 1 : post.likes + 1,
        }
        : post
    );
    setFilteredPosts(updatedPosts);
  };

  const handleBookmark = (postId: number) => {
    const updatedPosts = filteredPosts.map((post) =>
      post.id === postId
        ? {
          ...post,
          isBookmarked: !post.isBookmarked,
        }
        : post
    );
    setFilteredPosts(updatedPosts);
  };

  const handleShare = async (slug: string) => {
    if (!isClient) return;
    const url = `${window.location.origin}/blog/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      alert("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };
  const BreadCrumbsPath = [
    {
      id: 1,
      pathName: "Blogs",
      pathLink: "/pages/blogs",
    },
  ];


  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Healthy Horizons – Ceylon Hospital Blog
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Expert health insights and wellness tips for a healthier life.
            </p>
          </div>
        </div>
        <div>
          <BreadcrumbsComponent breadCrumbsPath={BreadCrumbsPath} />
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search and Filter */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
              <div className="relative w-full md:w-96">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search articles..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => {
                  const countData = categoryCounts.find(c => c.name === category.name) || { count: 0 };
                  return (
                    <button
                      key={category.id}
                      onClick={() =>
                        setActiveCategory(
                          category.name === "All" ? null : category.name
                        )
                      }
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category.name ||
                        (!activeCategory && category.name === "All")
                        ? "bg-green-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                    >
                      {category.name} ({countData.count})
                    </button>
                  );
                })}
              </div>
            </div>

            {searchQuery && (
              <div className="mb-4">
                <p className="text-gray-600">
                  Showing results for:{" "}
                  <span className="font-semibold">&quot;{searchQuery}&quot;</span> (
                  {filteredPosts.length} articles found)
                </p>
              </div>
            )}
          </div>

          {/* Blog Posts Grid */}
          {filteredPosts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {currentPosts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
                  >
                    <div className="relative h-48">
                      <img
                        src={post.blogImage}
                        alt={post.title}
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {post.category}
                        </span>
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-2">
                        <Link
                          to={`/pages/blogs/blog-article/${encryptId(post.id)}`}
                          className="hover:text-green-600 transition-colors"
                        >
                          {post.title}
                        </Link>
                      </h2>
                      <p className="text-gray-600 mb-4 flex-grow">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-2">
                          <FiCalendar className="h-4 w-4" />
                          <span>{post.date}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FiClock className="h-4 w-4" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                        <button
                          onClick={() => handleLike(post.id)}
                          className={`flex items-center space-x-1 ${post.isLiked
                            ? "text-red-500"
                            : "text-gray-500 hover:text-red-500"
                            }`}
                        >
                          <FiHeart className="h-5 w-5" />
                          <span>{post.likes}</span>
                        </button>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleBookmark(post.id)}
                            className={`${post.isBookmarked
                              ? "text-yellow-500"
                              : "text-gray-500 hover:text-yellow-500"
                              }`}
                          >
                            <FiBookmark className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleShare(post.slug)}
                            className="text-gray-500 hover:text-green-600"
                          >
                            <FiShare2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft className="mr-2 h-5 w-5" />
                    Previous
                  </button>
                  <div className="hidden md:flex space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-4 py-2 border text-sm font-medium rounded-md ${currentPage === page
                            ? "bg-green-600 text-white border-green-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <FiChevronRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                No articles found
              </h3>
              <p className="text-gray-600">
                Try adjusting your search or filter to find what you&apos;re looking
                for.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;




// "use client";
// import { useState, useEffect } from "react";
// import Head from "next/head";
// import Link from "next/link";
// import Image from "next/image";
// import {
//   FiSearch,
//   FiCalendar,
//   FiClock,
//   FiTag,
//   FiChevronLeft,
//   FiChevronRight,
//   FiBookmark,
//   FiShare2,
//   FiHeart,
// } from "react-icons/fi";

// import { BlogPost } from "@/types/blogTyps/BlogPostDetail";
// import { categories } from "@/data/blogData/BlogCategoriData";
// import { posts } from "@/data/blogData/BlogArticalsData";
// import { encryptId } from "@/components/encryption/encryption";

// const page = () => {
//   const [searchQuery, setSearchQuery] = useState("");
//   const [activeCategory, setActiveCategory] = useState<string | null>(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(posts);
//   const [isClient, setIsClient] = useState(false);
//   const postsPerPage = 6;

//   useEffect(() => {
//     setIsClient(true);
//   }, []);

//   useEffect(() => {
//     let results = [...posts];

//     // Filter by search query
//     if (searchQuery) {
//       results = results.filter(
//         (post) =>
//           post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
//           post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
//           post.tags.some((tag: string) =>
//             tag.toLowerCase().includes(searchQuery.toLowerCase())
//           )
//       );
//     }

//     // Filter by category
//     if (activeCategory && activeCategory !== "All") {
//       results = results.filter((post) => post.category === activeCategory);
//     }

//     setFilteredPosts(results);
//     setCurrentPage(1);
//   }, [searchQuery, activeCategory]);

//   // Pagination logic
//   const indexOfLastPost = currentPage * postsPerPage;
//   const indexOfFirstPost = indexOfLastPost - postsPerPage;
//   const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
//   const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

//   const handleLike = (postId: number) => {
//     const updatedPosts = filteredPosts.map((post) =>
//       post.id === postId
//         ? {
//             ...post,
//             isLiked: !post.isLiked,
//             likes: post.isLiked ? post.likes - 1 : post.likes + 1,
//           }
//         : post
//     );
//     setFilteredPosts(updatedPosts);
//   };

//   const handleBookmark = (postId: number) => {
//     const updatedPosts = filteredPosts.map((post) =>
//       post.id === postId
//         ? {
//             ...post,
//             isBookmarked: !post.isBookmarked,
//           }
//         : post
//     );
//     setFilteredPosts(updatedPosts);
//   };

//   const handleShare = async (slug: string) => {
//     if (!isClient) return;

//     const url = `${window.location.origin}/blog/${slug}`;
//     try {
//       await navigator.clipboard.writeText(url);
//       alert("Link copied to clipboard!");
//     } catch (err) {
//       console.error("Failed to copy URL: ", err);
//     }
//   };

//   return (
//     <>
//       <Head>
//         <title>Advanced Blog | Next.js + Tailwind CSS</title>
//         <meta
//           name="description"
//           content="Advanced blog page built with Next.js and Tailwind CSS"
//         />
//       </Head>

//       <div className="min-h-screen bg-gray-50">
//         {/* Hero Section */}
//         <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
//           <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
//             <h1 className="text-4xl md:text-5xl font-bold mb-4">
//               Healthy Horizons – Ceylon Hospital Blog
//             </h1>
//             <p className="text-xl md:text-2xl max-w-3xl mx-auto">
//               Expert health insights and wellness tips for a healthier life.
//             </p>
//           </div>
//         </div>

//         {/* Main Content */}
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//           {/* Search and Filter */}
//           <div className="mb-12">
//             <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
//               <div className="relative w-full md:w-96">
//                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                   <FiSearch className="text-gray-400" />
//                 </div>
//                 <input
//                   type="text"
//                   placeholder="Search articles..."
//                   className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                 />
//               </div>
//               <div className="flex flex-wrap gap-2">
//                 {categories.map((category) => (
//                   <button
//                     key={category.id}
//                     onClick={() =>
//                       setActiveCategory(
//                         category.name === "All" ? null : category.name
//                       )
//                     }
//                     className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
//                       activeCategory === category.name ||
//                       (!activeCategory && category.name === "All")
//                         ? "bg-green-600 text-white"
//                         : "bg-gray-200 text-gray-700 hover:bg-gray-300"
//                     }`}
//                   >
//                     {category.name} ({category.count})
//                   </button>
//                 ))}
//               </div>
//             </div>

//             {searchQuery && (
//               <div className="mb-4">
//                 <p className="text-gray-600">
//                   Showing results for:{" "}
//                   <span className="font-semibold">"{searchQuery}"</span> (
//                   {filteredPosts.length} articles found)
//                 </p>
//               </div>
//             )}
//           </div>

//           {/* Blog Posts Grid */}
//           {filteredPosts.length > 0 ? (
//             <>
//               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
//                 {currentPosts.map((post) => (
//                   <article
//                     key={post.id}
//                     className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
//                   >
//                     <div className="relative h-48">
//                       <Image
//                         src={post.blogImage}
//                         alt={post.title}
//                         priority
//                         fill
//                         className="object-cover"
//                         placeholder="blur"
//                         sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
//                         quality={85}
//                       />
//                     </div>
//                     <div className="p-6 flex flex-col flex-grow">
//                       <div className="flex flex-wrap items-center gap-2 mb-3">
//                         <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
//                           {post.category}
//                         </span>
//                         {post.tags.map((tag) => (
//                           <span
//                             key={tag}
//                             className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
//                           >
//                             {tag}
//                           </span>
//                         ))}
//                       </div>
//                       <h2 className="text-xl font-bold text-gray-900 mb-2">
//                         <Link
//                           href={`/blogs/${encryptId(post.id)}`}
//                           className="hover:text-green-600 transition-colors"
//                         >
//                           {post.title}
//                         </Link>
//                       </h2>
//                       <p className="text-gray-600 mb-4 flex-grow">
//                         {post.excerpt}
//                       </p>
//                       <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
//                         <div className="flex items-center space-x-2">
//                           <FiCalendar className="h-4 w-4" />
//                           <span>{post.date}</span>
//                         </div>
//                         <div className="flex items-center space-x-2">
//                           <FiClock className="h-4 w-4" />
//                           <span>{post.readTime}</span>
//                         </div>
//                       </div>
//                       <div className="flex items-center justify-between border-t border-gray-100 pt-4">
//                         <button
//                           onClick={() => handleLike(post.id)}
//                           className={`flex items-center space-x-1 ${
//                             post.isLiked
//                               ? "text-red-500"
//                               : "text-gray-500 hover:text-red-500"
//                           }`}
//                         >
//                           <FiHeart className="h-5 w-5" />

//                           <span>{post.likes}</span>
//                         </button>
//                         <div className="flex space-x-3">
//                           <button
//                             onClick={() => handleBookmark(post.id)}
//                             className={`${
//                               post.isBookmarked
//                                 ? "text-yellow-500"
//                                 : "text-gray-500 hover:text-yellow-500"
//                             }`}
//                           >
//                             <FiBookmark className="h-5 w-5" />
//                           </button>
//                           <button
//                             onClick={() => handleShare(post.slug)}
//                             className="text-gray-500 hover:text-green-600"
//                           >
//                             <FiShare2 className="h-5 w-5" />
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </article>
//                 ))}
//               </div>

//               {/* Pagination */}
//               {totalPages > 1 && (
//                 <div className="flex items-center justify-between border-t border-gray-200 pt-6">
//                   <button
//                     onClick={() =>
//                       setCurrentPage((prev) => Math.max(prev - 1, 1))
//                     }
//                     disabled={currentPage === 1}
//                     className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
//                   >
//                     <FiChevronLeft className="mr-2 h-5 w-5" />
//                     Previous
//                   </button>
//                   <div className="hidden md:flex space-x-2">
//                     {Array.from({ length: totalPages }, (_, i) => i + 1).map(
//                       (page) => (
//                         <button
//                           key={page}
//                           onClick={() => setCurrentPage(page)}
//                           className={`px-4 py-2 border text-sm font-medium rounded-md ${
//                             currentPage === page
//                               ? "bg-green-600 text-white border-green-600"
//                               : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
//                           }`}
//                         >
//                           {page}
//                         </button>
//                       )
//                     )}
//                   </div>
//                   <button
//                     onClick={() =>
//                       setCurrentPage((prev) => Math.min(prev + 1, totalPages))
//                     }
//                     disabled={currentPage === totalPages}
//                     className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
//                   >
//                     Next
//                     <FiChevronRight className="ml-2 h-5 w-5" />
//                   </button>
//                 </div>
//               )}
//             </>
//           ) : (
//             <div className="text-center py-12">
//               <h3 className="text-xl font-medium text-gray-900 mb-2">
//                 No articles found
//               </h3>
//               <p className="text-gray-600">
//                 Try adjusting your search or filter to find what you're looking
//                 for.
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </>
//   );
// };

// export default page;
